import React, { useState } from "react";
import { IMAGES } from "@/lib/images";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { CommercialMaintenance } from "@/components/CommercialMaintenance";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";
import { Wrench, Search, Cog, RefreshCw, Truck, Check, Phone } from "lucide-react";

const SERVICES = [
  { icon: Search, title: "Diagnostics", copy: "Comprehensive checks to identify any issues with your mower." },
  { icon: Wrench, title: "Repairs", copy: "Expert repairs using genuine parts to restore performance." },
  { icon: Cog, title: "Blade replacement", copy: "Professional blade changes for optimal cutting." },
  { icon: RefreshCw, title: "Software updates", copy: "Firmware updates and optimisation for improved functionality." },
];

const DOMESTIC_INCLUDES = [
  "Full strip-down and deep clean",
  "Cutting system stripped, cleaned & rebuilt",
  "Motor, bearings & battery health checked",
  "Software and firmware updated",
  "Full diagnostics & operational testing",
  "Detailed service report provided",
];

const COMMERCIAL_INCLUDES = [
  "Regular blade changes & full cleaning",
  "Cutting head strip-down & deep clean",
  "Component, safety & performance checks",
  "Charging station cleaned & voltage tested",
  "Priority breakdown response & phone support",
  "Winter collection, service & secure storage",
];

export default function Service() {
  const site = useSite();
  const [enq, setEnq] = useState(false);
  return (
    <div>
      <section className="relative bg-brand-navy text-white">
        <div className="absolute inset-0 opacity-25"><img src={IMAGES.grass} alt="" className="h-full w-full object-cover" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <span className="font-mono-spec text-xs uppercase tracking-widest text-white">Service & Repairs</span>
          <h1 className="mt-3 font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Thorough workshop servicing</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/80">Your robotic mower works outdoors for hundreds of hours a season. We strip down, clean, inspect, diagnose, rebuild and fully test every mower we work on — for all major brands.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Our services include</h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="rounded-2xl border border-brand-navy/10 bg-white p-6">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy/10 text-brand-navy"><s.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-brand-navy/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-3xl border border-brand-navy/10 p-8 flex flex-col">
            <h3 className="font-display font-bold text-2xl text-brand-ink">Full health check & annual service</h3>
            <p className="mt-2 text-muted-foreground">For domestic robotic lawnmowers, carried out in our specialist workshop.</p>
            <div className="mt-4 flex items-baseline gap-2"><span className="font-display font-extrabold text-3xl text-brand-navy">€200 + VAT</span><span className="text-muted-foreground">(€227 incl VAT)</span></div>
            <p className="mt-3 text-sm text-muted-foreground">Covers technician time for the full service, inspection, diagnostics, rebuild & testing. Blades, bearings & parts are quoted separately — we never carry out extra chargeable work without your approval.</p>
            <ul className="mt-5 space-y-2 flex-1">{DOMESTIC_INCLUDES.map((i) => <li key={i} className="flex items-start gap-2 text-brand-ink"><Check className="h-4 w-4 text-brand-navy mt-1 shrink-0" /> {i}</li>)}</ul>
            <Button onClick={() => setEnq(true)} className="mt-6 w-fit bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold" data-testid="service-book-domestic">Book a service</Button>
          </div>
          <div className="rounded-3xl bg-brand-navy text-white p-8 flex flex-col">
            <h3 className="font-display font-bold text-2xl">Commercial monthly maintenance</h3>
            <p className="mt-2 text-white/80">Keeping commercial mowers working — not waiting for something to go wrong.</p>
            <p className="mt-4 text-sm text-white/70">Tailored to your site and priced by machine count, site size & support level.</p>
            <ul className="mt-5 space-y-2 flex-1">{COMMERCIAL_INCLUDES.map((i) => <li key={i} className="flex items-start gap-2 text-white/90"><Check className="h-4 w-4 text-white mt-1 shrink-0" /> {i}</li>)}</ul>
            <Button onClick={() => setEnq(true)} className="mt-6 w-fit bg-white hover:bg-white/90 text-brand-navy font-semibold" data-testid="service-book-commercial">Discuss a plan</Button>
          </div>
        </div>
      </section>

      <CommercialMaintenance onEnquire={() => setEnq(true)} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <div>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-brand-ink">Getting your mower to our workshop</h2>
          <p className="mt-4 text-muted-foreground">Drop it in yourself or let us collect it — whichever suits you. We're based in Fyanstown, Kells, Co. Meath.</p>
          <div className="mt-6 space-y-4">
            <div className="rounded-2xl border border-brand-navy/10 bg-white p-5 flex items-start gap-3"><Truck className="h-6 w-6 text-brand-navy shrink-0" /><div><div className="font-semibold text-brand-ink">DPD Collection — €30</div><div className="text-sm text-muted-foreground">Including VAT, each way.</div></div></div>
            <div className="rounded-2xl border border-brand-navy/10 bg-white p-5 flex items-start gap-3"><Truck className="h-6 w-6 text-brand-navy shrink-0" /><div><div className="font-semibold text-brand-ink">Our own collection service</div><div className="text-sm text-muted-foreground">Available depending on location, priced according to distance.</div></div></div>
          </div>
        </div>
        <div className="rounded-3xl bg-brand-white border border-brand-navy/10 p-8">
          <h3 className="font-display font-bold text-2xl text-brand-ink">Need a Repair?</h3>
          <p className="mt-3 text-muted-foreground">You don't need to wait for your annual service if your mower develops a problem. Our workshop provides professional diagnostics and repair services for robotic lawnmowers from leading manufacturers.</p>
          <p className="mt-3 text-muted-foreground">If you're experiencing an error, charging problem, unusual noise, cutting issue, battery problem or another fault, contact our team and we'll advise you on the next step.</p>
          <h4 className="mt-6 font-display font-semibold text-lg text-brand-navy">Look After Your Mower Before Something Goes Wrong</h4>
          <p className="mt-2 text-muted-foreground">Regular professional servicing gives us the opportunity to identify wear and developing issues that may otherwise go unnoticed. Book your Annual Service or speak to us about a Maintenance Plan.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => setEnq(true)} className="bg-brand-navy hover:bg-brand-navy/90 text-white" data-testid="service-repair-enquire">Request a repair</Button>
            <Button asChild variant="outline" className="border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white"><a href={`tel:${site.phone?.replace(/\s/g, "")}`}><Phone className="mr-2 h-4 w-4" /> {site.phone}</a></Button>
          </div>
        </div>
      </section>

      <EnquiryDialog open={enq} onOpenChange={setEnq} type="service" sourcePage="service" />
    </div>
  );
}
